import { useQuery } from "@tanstack/react-query";
import { useAppSelector } from "../State/Hooks/reduxHooks";
import { selectClientValue } from "../State/Slices/SelectedClientSlice";
import { abacApiFetcher, abacPermissionControllerPath } from "./queryHelpers";
import { useCurrentUser } from "./UserQueries";
import { AbacAppKey } from "../Constants/AbacConstants";
import { useAuth0 } from "@auth0/auth0-react";

/**
 * Fetches and returns the user's authorized features from the ABAC system.
 *
 * @returns UseQueryResult<string[]>
 *
 * @example
 * ```ts
 * const { data: userAuthorizedFeatures } = useGetUserFeatures();
 * const isUserAuthorized = userAuthorizedFeatures?.has(`${AbacAppKey}:${featureName}`);
 * ```
 */
export function useGetUserFeatures() {
  const { email: userEmail } = useCurrentUser();
  const { clientId } = useAppSelector(selectClientValue);
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  return useQuery<Set<string>>(
    ["userFeatures", { clientId, userEmail }],
    async () => {
      const accessToken = await getAccessTokenSilently();
      return new Set(
        (await abacApiFetcher(
          `${abacPermissionControllerPath}GetUserFeatures/${AbacAppKey}/${clientId}/${userEmail}`,
          { headers: { Authorization: `Bearer ${accessToken}` } },
        )) as string[],
      );
    },
    {
      enabled: !!clientId && !!userEmail && isAuthenticated,
      staleTime: 1000 * 60 * 5, // 5 minutes
    },
  );
}
